import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Select,
  Skeleton,
  Space,
  Typography
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import MoneyInput from "../../../serviceProvider/components/accounting/shared/MoneyInput";
import { formatCents } from "../../../serviceProvider/components/accounting/shared/money";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { correctionReasonLabels } from "../../../utils/invoiceStatusArray";
import { creditLimitText, invoiceErrorText } from "./invoiceErrors";
import { Error, Success } from "../../Messages/Messages";
import "./InvoiceDialogs.css";

const roundHalfUp = v => Math.sign(v) * Math.round(Math.abs(v) + Number.EPSILON);

/** Net of a credited original line, computed like the server (unit price × qty − line discount). */
const lineNet = (line, quantity) => {
  const net = roundHalfUp(Number(line.unitPrice || 0) * Number(quantity || 0));
  return line.discount ? net - roundHalfUp((net * Number(line.discount)) / 100) : net;
};

const emptyFree = rate => ({
  description: "",
  quantity: 1,
  unit: "",
  unitPrice: null,
  taxRate: rate
});

/**
 * UI-1: Rechnungskorrektur. Original lines with the remaining creditable quantity, free lines
 * at a rate of the original, a mandatory reason, live totals per rate and what the correction
 * does with the money (reduces the open amount / creates a customer credit).
 */
const CorrectionDialog = ({ open, invoice, onClose, prefill = null }) => {
  const { t: ta } = useTranslation("accounting");
  const history = useHistory();
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [freeLines, setFreeLines] = useState([]);
  const [reason, setReason] = useState({ code: undefined, text: "" });
  const [limitErrors, setLimitErrors] = useState([]);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open || !invoice?._id) return;
    setQuantities({});
    // PAY-2: opened from the payment dialog with the customer's deduction as free lines
    setFreeLines(Array.isArray(prefill?.freeLines) ? prefill.freeLines.map(f => ({ ...f })) : []);
    setReason({ code: prefill?.reason?.code, text: prefill?.reason?.text || "" });
    setLimitErrors([]);
    setError("");
    setTouched(false);
    setLoading(true);
    accounting
      .creditable(invoice._id)
      .then(res => setAvail(res.data))
      .catch(err => setError(invoiceErrorText(ta, err)))
      .finally(() => setLoading(false));
  }, [open, invoice?._id]);

  const taxable = (avail?.invoice?.taxMode || invoice?.taxMode || "regular") === "regular";
  const rates = useMemo(() => {
    const list = (avail?.byRate || []).map(r => Number(r.rate));
    return list.length ? list : [taxable ? 19 : 0];
  }, [avail, taxable]);

  const totals = useMemo(() => {
    const byRate = new Map();
    const add = (rate, net) => byRate.set(rate, (byRate.get(rate) || 0) + net);
    for (const line of avail?.lines || []) {
      const q = Number(quantities[line.index] || 0);
      if (q > 0) add(taxable ? Number(line.taxRate) : 0, lineNet(line, q));
    }
    for (const f of freeLines) {
      const net = roundHalfUp(Number(f.unitPrice || 0) * Number(f.quantity || 0));
      if (net) add(taxable ? Number(f.taxRate) : 0, net);
    }
    const groups = [...byRate.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([rate, net]) => {
        const tax = taxable ? roundHalfUp((net * rate) / 100) : 0;
        const r = (avail?.byRate || []).find(x => Number(x.rate) === rate);
        return { rate, net, tax, gross: net + tax, remainingNet: r ? r.remainingNet : 0 };
      });
    const gross = groups.reduce((s, g) => s + g.gross, 0);
    return { groups, gross };
  }, [avail, quantities, freeLines, taxable]);

  const open_ = Number(avail?.invoice?.open ?? invoice?.amounts?.open ?? 0);
  const overRate = totals.groups.filter(g => g.net > g.remainingNet);
  const overTotal = avail && totals.gross > avail.remainingGross;
  const overLine = (avail?.lines || []).filter(
    l => Number(quantities[l.index] || 0) > l.remaining + 1e-9
  );
  const freeIncomplete = freeLines.some(f => !f.description?.trim() || !Number(f.unitPrice));
  const reasonMissing = !reason.code || (reason.code === "other" && !reason.text.trim());
  const nothing = totals.gross <= 0;
  const blocked =
    nothing || reasonMissing || freeIncomplete || overRate.length || overTotal || overLine.length;

  const setQty = (index, value) => setQuantities(prev => ({ ...prev, [index]: value ?? 0 }));
  const setFree = (i, key, value) =>
    setFreeLines(prev => prev.map((f, j) => (j === i ? { ...f, [key]: value } : f)));

  const creditAll = () =>
    setQuantities(Object.fromEntries((avail?.lines || []).map(l => [l.index, l.remaining])));

  const submit = async send => {
    setTouched(true);
    if (blocked) return;
    setBusy(true);
    setLimitErrors([]);
    setError("");
    let created = null;
    try {
      const res = await accounting.createCreditNote(invoice._id, {
        lines: (avail?.lines || [])
          .filter(l => Number(quantities[l.index] || 0) > 0)
          .map(l => ({ index: l.index, quantity: Number(quantities[l.index]) })),
        freeLines: freeLines.map(f => ({
          description: f.description.trim(),
          quantity: Number(f.quantity || 0),
          unit: f.unit || "",
          unitPrice: Math.round(Number(f.unitPrice || 0)),
          taxRate: Number(f.taxRate)
        })),
        reason: { code: reason.code, text: reason.text.trim() }
      });
      created = res.data.invoice;
      if (!send) {
        Success(ta("Invoice correction saved as draft"));
        onClose(true);
        history.push(`/invoices/details/${created._id}`);
        return;
      }
      const sent = await accounting.sendInvoice(created._id, { email: true });
      Success(`${ta("Invoice correction sent")}: ${sent.data.invoice.invoiceNumber}`);
      onClose(true);
      // GS-5: the details page asks what to do with a resulting customer credit
      history.push(`/invoices/details/${created._id}`, { creditPrompt: true });
    } catch (err) {
      const info = accounting.errorInfo(err);
      if (created) {
        // The draft exists: open it; the send dialog there shows what is missing.
        Error(invoiceErrorText(ta, err));
        onClose(true);
        history.push(`/invoices/details/${created._id}`, { openSend: true });
      } else if (info.code === "CREDIT_EXCEEDS_INVOICE") {
        setLimitErrors(info.details?.errors || []);
        if (info.details?.available) setAvail(info.details.available);
      } else setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  const result =
    totals.gross <= 0
      ? null
      : open_ <= 0
        ? {
            type: "warning",
            text: ta("The invoice is paid: this creates a customer credit of {{amount}}", {
              amount: formatCents(totals.gross)
            })
          }
        : totals.gross <= open_
          ? {
              type: "info",
              text: ta("Reduces the open amount from {{from}} to {{to}}", {
                from: formatCents(open_),
                to: formatCents(open_ - totals.gross)
              })
            }
          : {
              type: "warning",
              text: ta("Reduces the open amount to 0 and creates a customer credit of {{amount}}", {
                amount: formatCents(totals.gross - open_)
              })
            };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={`${ta("Invoice correction")} – ${invoice?.invoiceNumber || ""}`}
      open={open}
      width={820}
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => onClose(false)}>
          {ta("Cancel")}
        </Button>,
        <Button key="draft" loading={busy} onClick={() => submit(false)}>
          {ta("Save draft")}
        </Button>,
        <Button key="send" type="primary" loading={busy} onClick={() => submit(true)}>
          {ta("Send now")}
        </Button>
      ]}
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <div className="invoice-dialog__body">
          {error ? <Alert type="error" showIcon message={error} /> : null}
          {limitErrors.length ? (
            <Alert
              type="error"
              showIcon
              message={ta("The invoice correction exceeds what is left of the invoice.")}
              description={
                <ul className="invoice-dialog__list">
                  {limitErrors.map((e, i) => (
                    <li key={i}>{creditLimitText(ta, e)}</li>
                  ))}
                </ul>
              }
            />
          ) : null}

          <section>
            <div className="invoice-dialog__section-head">
              <Typography.Title level={5}>{ta("Original lines")}</Typography.Title>
              <Button size="small" onClick={creditAll}>
                {ta("Credit everything left")}
              </Button>
            </div>
            {(avail?.lines || []).length ? null : (
              <Typography.Text type="secondary">{ta("The invoice has no lines")}</Typography.Text>
            )}
            {(avail?.lines || []).map(line => {
              const q = Number(quantities[line.index] || 0);
              const over = q > line.remaining + 1e-9;
              return (
                <div key={line.index} className="invoice-dialog__line">
                  <div className="invoice-dialog__line-text">
                    <strong>
                      {line.index + 1}. {line.description || "–"}
                    </strong>
                    <span className="invoice-dialog__meta">
                      {ta("Billed")}: {line.quantity} {line.unit} · {ta("Credited")}:{" "}
                      {line.credited} · {ta("Left")}: {line.remaining} ·{" "}
                      {formatCents(line.unitPrice)}
                      {taxable ? ` · ${line.taxRate} %` : ""}
                    </span>
                    {over ? (
                      <Typography.Text type="danger">
                        {ta("Line {{line}}: only {{remaining}} left to credit", {
                          line: line.index + 1,
                          remaining: line.remaining
                        })}
                      </Typography.Text>
                    ) : null}
                  </div>
                  <div className="invoice-dialog__line-input">
                    <InputNumber
                      aria-label={ta("Quantity to credit")}
                      min={0}
                      max={line.remaining}
                      step={1}
                      disabled={line.remaining <= 0}
                      status={over ? "error" : undefined}
                      value={quantities[line.index] || null}
                      placeholder="0"
                      onChange={v => setQty(line.index, v)}
                      style={{ width: "100%" }}
                    />
                    <span className="invoice-dialog__meta">
                      {q > 0 ? formatCents(lineNet(line, q)) : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </section>

          <section>
            <div className="invoice-dialog__section-head">
              <Typography.Title level={5}>{ta("Free lines")}</Typography.Title>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setFreeLines(prev => [...prev, emptyFree(rates[0])])}
              >
                {ta("Add line")}
              </Button>
            </div>
            <Typography.Text type="secondary" className="invoice-dialog__hint">
              {ta(
                "For example a price reduction because of defects. The VAT rate must occur on the original invoice."
              )}
            </Typography.Text>
            {freeLines.map((f, i) => (
              <div key={i} className="invoice-dialog__free">
                <Input
                  className="invoice-dialog__free-desc"
                  placeholder={ta("Description")}
                  aria-label={ta("Description")}
                  value={f.description}
                  status={touched && !f.description?.trim() ? "error" : undefined}
                  onChange={e => setFree(i, "description", e.target.value)}
                />
                <label className="invoice-dialog__field">
                  <span>{ta("Quantity")}</span>
                  <InputNumber
                    min={0}
                    value={f.quantity}
                    onChange={v => setFree(i, "quantity", v ?? 0)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label className="invoice-dialog__field">
                  <span>{ta("Unit price (net)")}</span>
                  <MoneyInput
                    min={0}
                    value={f.unitPrice}
                    status={touched && !Number(f.unitPrice) ? "error" : undefined}
                    onChange={v => setFree(i, "unitPrice", v)}
                  />
                </label>
                <label className="invoice-dialog__field">
                  <span>{ta("VAT rate")}</span>
                  <Select
                    value={f.taxRate}
                    onChange={v => setFree(i, "taxRate", v)}
                    options={rates.map(r => ({ value: r, label: `${r} %` }))}
                    disabled={!taxable}
                  />
                </label>
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  aria-label={ta("Remove line")}
                  className="invoice-dialog__free-remove"
                  onClick={() => setFreeLines(prev => prev.filter((_, j) => j !== i))}
                />
              </div>
            ))}
          </section>

          <section>
            <Typography.Title level={5}>{ta("Reason")}</Typography.Title>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select
                style={{ width: "100%" }}
                placeholder={ta("Choose a reason")}
                value={reason.code}
                status={touched && !reason.code ? "error" : undefined}
                onChange={code => setReason(prev => ({ ...prev, code }))}
                options={Object.entries(correctionReasonLabels).map(([value, label]) => ({
                  value,
                  label: ta(label)
                }))}
              />
              <Input.TextArea
                rows={2}
                maxLength={1000}
                placeholder={ta("Explain the correction (printed on the document)")}
                value={reason.text}
                status={
                  touched && reason.code === "other" && !reason.text.trim() ? "error" : undefined
                }
                onChange={e => setReason(prev => ({ ...prev, text: e.target.value }))}
              />
              {touched && reasonMissing ? (
                <Typography.Text type="danger">
                  {ta("An invoice correction needs a reason.")}
                </Typography.Text>
              ) : null}
            </Space>
          </section>

          <section className="invoice-dialog__totals">
            {totals.groups.map(g => (
              <div key={g.rate} className="invoice-dialog__total-row">
                <span>
                  {taxable ? `${g.rate} %: ` : ""}
                  {formatCents(g.net)} {ta("net")}
                  {taxable ? ` + ${formatCents(g.tax)} ${ta("VAT")}` : ""}
                </span>
                <span>
                  {g.net > g.remainingNet ? (
                    <Typography.Text type="danger">
                      {ta("{{rate}} % VAT: only {{amount}} net left to credit", {
                        rate: g.rate,
                        amount: formatCents(g.remainingNet)
                      })}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">
                      {ta("Left")}: {formatCents(g.remainingNet)}
                    </Typography.Text>
                  )}
                </span>
              </div>
            ))}
            <div className="invoice-dialog__total-row invoice-dialog__total-row--sum">
              <strong>{ta("Correction total (gross)")}</strong>
              <strong>{formatCents(totals.gross)}</strong>
            </div>
            {avail ? (
              <div className="invoice-dialog__total-row">
                <Typography.Text type="secondary">{ta("Still creditable (gross)")}</Typography.Text>
                <Typography.Text type={overTotal ? "danger" : "secondary"}>
                  {formatCents(avail.remainingGross)}
                </Typography.Text>
              </div>
            ) : null}
            {result ? <Alert type={result.type} showIcon message={result.text} /> : null}
            {touched && nothing ? (
              <Typography.Text type="danger">
                {ta("Enter a quantity or a free line to credit")}
              </Typography.Text>
            ) : null}
          </section>
        </div>
      )}
    </Modal>
  );
};

export default CorrectionDialog;
